import {
  hashtagCount,
  PLAN_CHANNEL_HASHTAG_LIMITS,
  PLAN_CHANNEL_LABELS,
  PLAN_CHANNEL_TEXT_LIMITS,
  PLAN_CHANNELS,
  PLAN_STATUS_LABELS,
  PLAN_STATUSES,
  type PlanChannel,
  type PlannedPost,
  type PlanStatus,
  postTextLength,
} from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  FileImage,
  Film,
  List,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { type DragEvent, type ReactNode, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm";
import { ContextMenuPopup, useContextMenu } from "../components/ui/context-menu";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import {
  DropdownItem,
  DropdownLabel,
  DropdownSeparator,
  DropdownSub,
  DropdownSubContent,
  DropdownSubTrigger,
} from "../components/ui/dropdown";
import { Input, Select, Textarea } from "../components/ui/input";
import { orpc } from "../lib/api";
import { useOrgId, useOrgRole } from "../lib/org";
import { cn, errorMessage } from "../lib/utils";

/**
 * „Planung“: content calendar of the organization (planned posts per day).
 * Month grid (drag entries to another day) or a compact list; viewers only
 * look. Nothing is posted to the networks.
 */

const CHANNEL_STYLE: Record<PlanChannel, { short: string; className: string }> = {
  instagram: {
    short: "IG",
    className: "bg-gradient-to-br from-amber-400 via-pink-500 to-violet-600 text-white",
  },
  tiktok: { short: "TT", className: "bg-zinc-900 text-white" },
  linkedin: { short: "in", className: "bg-sky-700 text-white" },
  facebook: { short: "f", className: "bg-blue-600 text-white" },
  youtube: { short: "▶", className: "bg-red-600 text-white" },
  x: { short: "X", className: "bg-black text-white" },
  other: { short: "•", className: "bg-zinc-400 text-white" },
};

const STATUS_STYLE: Record<PlanStatus, string> = {
  idea: "bg-zinc-100 text-zinc-700 border-zinc-200",
  in_progress: "bg-amber-50 text-amber-800 border-amber-200",
  ready: "bg-sky-50 text-sky-800 border-sky-200",
  published: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function ChannelBadge({ channel, className }: { channel: PlanChannel; className?: string }) {
  const style = CHANNEL_STYLE[channel];
  return (
    <span
      title={PLAN_CHANNEL_LABELS[channel]}
      className={cn(
        "inline-flex size-4 shrink-0 items-center justify-center rounded-[4px] text-[8px] leading-none font-bold",
        style.className,
        className,
      )}
    >
      {style.short}
    </span>
  );
}

const dayKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const timeOf = (date: Date) =>
  `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

/** Monday-based weeks covering the month. */
function monthGrid(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - offset);
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const days = offset + last.getDate();
  const weeks = Math.ceil(days / 7);
  return Array.from(
    { length: weeks * 7 },
    (_, index) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + index),
  );
}

type Draft = {
  id: string | null;
  title: string;
  date: string;
  time: string;
  channel: PlanChannel;
  status: PlanStatus;
  caption: string;
  notes: string;
  documentId: string;
  assigneeId: string;
};

function draftFor(post: PlannedPost | null, day?: Date): Draft {
  if (post) {
    return {
      id: post.id,
      title: post.title,
      date: dayKey(post.scheduledAt),
      time: timeOf(post.scheduledAt),
      channel: post.channel,
      status: post.status,
      caption: post.caption,
      notes: post.notes,
      documentId: post.document?.id ?? "",
      assigneeId: post.assignee?.id ?? "",
    };
  }
  return {
    id: null,
    title: "",
    date: dayKey(day ?? new Date()),
    time: "10:00",
    channel: "instagram",
    status: "idea",
    caption: "",
    notes: "",
    documentId: "",
    assigneeId: "",
  };
}

export function PlanningPage() {
  const orgId = useOrgId();
  const { can } = useOrgRole(orgId);
  const canEdit = can("document.update");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [view, setView] = useState<"month" | "list">("month");
  const [channels, setChannels] = useState<PlanChannel[]>([]);
  const [statuses, setStatuses] = useState<PlanStatus[]>([]);
  const { plan } = useSearch({ strict: false }) as { plan?: string };
  const [draft, setDraft] = useState<Draft | null>(() =>
    plan && canEdit ? { ...draftFor(null), documentId: plan } : null,
  );
  const [dragOver, setDragOver] = useState<string | null>(null);

  const days = useMemo(() => monthGrid(month), [month]);
  const from = days[0]!;
  const last = days[days.length - 1]!;
  const to = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1);
  const input = {
    orgId,
    from,
    to,
    ...(channels.length ? { channels } : {}),
    ...(statuses.length ? { statuses } : {}),
  };
  const posts = useQuery(orpc.contentPlan.list.queryOptions({ input }));
  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.contentPlan.key() });

  const byDay = useMemo(() => {
    const map = new Map<string, PlannedPost[]>();
    for (const post of posts.data ?? []) {
      const key = dayKey(post.scheduledAt);
      map.set(key, [...(map.get(key) ?? []), post]);
    }
    return map;
  }, [posts.data]);

  const update = useMutation(
    orpc.contentPlan.update.mutationOptions({
      onSuccess: () => invalidate(),
      onError: (error) => {
        toast.error(errorMessage(error));
        void invalidate();
      },
    }),
  );

  const onDrop = (event: DragEvent, day: Date) => {
    event.preventDefault();
    setDragOver(null);
    const id = event.dataTransfer.getData("application/x-planned-post");
    const post = posts.data?.find((item) => item.id === id);
    if (!post || dayKey(post.scheduledAt) === dayKey(day)) return;
    const scheduledAt = new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      post.scheduledAt.getHours(),
      post.scheduledAt.getMinutes(),
    );
    // Optimistic: move it right away.
    queryClient.setQueryData(
      orpc.contentPlan.list.queryKey({ input }),
      (old: PlannedPost[] | undefined) =>
        old?.map((item) => (item.id === id ? { ...item, scheduledAt } : item)),
    );
    update.mutate({ orgId, id, scheduledAt });
  };

  const today = dayKey(new Date());
  const monthLabel = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" }).format(
    month,
  );
  const shiftMonth = (delta: number) =>
    setMonth(new Date(month.getFullYear(), month.getMonth() + delta, 1));
  const toggle = <T,>(list: T[], value: T) =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
  const openPost = (post: PlannedPost) => setDraft(draftFor(post));
  const postMenu = useContextMenu<PlannedPost>();
  const [removing, setRemoving] = useState<PlannedPost | null>(null);
  const remove = useMutation(
    orpc.contentPlan.remove.mutationOptions({
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );

  return (
    <>
      <PageHeader
        title="Planung"
        description="Redaktionsplan der Organisation: wann welcher Beitrag auf welchem Kanal erscheinen soll. Veröffentlicht wird nichts automatisch."
        actions={
          canEdit ? (
            <Button onClick={() => setDraft(draftFor(null))}>
              <Plus /> Beitrag planen
            </Button>
          ) : null
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Vorheriger Monat"
            onClick={() => shiftMonth(-1)}
          >
            <ChevronLeft />
          </Button>
          <h2 className="w-44 text-center text-lg font-semibold text-zinc-900 capitalize">
            {monthLabel}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Nächster Monat"
            onClick={() => shiftMonth(1)}
          >
            <ChevronRight />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const now = new Date();
              setMonth(new Date(now.getFullYear(), now.getMonth(), 1));
            }}
          >
            Heute
          </Button>
        </div>
        <div className="flex rounded-lg bg-zinc-100 p-0.5 text-sm">
          {(
            [
              ["month", "Monat", <CalendarDays key="m" className="size-4" />],
              ["list", "Liste", <List key="l" className="size-4" />],
            ] as const
          ).map(([id, label, icon]) => (
            <button
              key={id}
              type="button"
              onClick={() => setView(id)}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium",
                view === id ? "bg-white shadow-sm" : "text-zinc-600 hover:text-zinc-900",
              )}
            >
              {icon}
              {label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1">
          {PLAN_CHANNELS.map((channel) => (
            <FilterChip
              key={channel}
              active={channels.includes(channel)}
              onClick={() => setChannels(toggle(channels, channel))}
            >
              <ChannelBadge channel={channel} /> {PLAN_CHANNEL_LABELS[channel]}
            </FilterChip>
          ))}
        </div>
        <div className="flex w-full flex-wrap items-center justify-end gap-1">
          {PLAN_STATUSES.map((status) => (
            <FilterChip
              key={status}
              active={statuses.includes(status)}
              onClick={() => setStatuses(toggle(statuses, status))}
            >
              {PLAN_STATUS_LABELS[status]}
            </FilterChip>
          ))}
        </div>
      </div>

      {view === "month" ? (
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
          <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50 text-xs font-semibold text-zinc-500">
            {WEEKDAYS.map((day) => (
              <div key={day} className="px-2 py-1.5">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const key = dayKey(day);
              const inMonth = day.getMonth() === month.getMonth();
              const items = byDay.get(key) ?? [];
              return (
                // biome-ignore lint/a11y/noStaticElementInteractions: drop target; the dialog is the keyboard way
                <div
                  key={key}
                  data-day={key}
                  onDragOver={(event) => {
                    if (!canEdit) return;
                    event.preventDefault();
                    setDragOver(key);
                  }}
                  onDragLeave={() => setDragOver((current) => (current === key ? null : current))}
                  onDrop={(event) => onDrop(event, day)}
                  className={cn(
                    "group relative min-h-28 border-r border-b border-zinc-100 p-1.5 [&:nth-child(7n)]:border-r-0",
                    !inMonth && "bg-zinc-50/60",
                    dragOver === key && "bg-brand-50 ring-2 ring-brand-400 ring-inset",
                  )}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span
                      className={cn(
                        "flex size-6 items-center justify-center rounded-full text-xs tabular-nums",
                        inMonth ? "text-zinc-700" : "text-zinc-400",
                        key === today && "bg-brand-600 font-semibold text-white",
                      )}
                    >
                      {day.getDate()}
                    </span>
                    {canEdit ? (
                      <button
                        type="button"
                        aria-label={`Beitrag am ${day.toLocaleDateString("de-DE")} planen`}
                        onClick={() => setDraft(draftFor(null, day))}
                        className="rounded p-0.5 text-zinc-400 opacity-0 group-hover:opacity-100 hover:bg-zinc-100 hover:text-zinc-700 focus:opacity-100"
                      >
                        <Plus className="size-3.5" />
                      </button>
                    ) : null}
                  </div>
                  <div className="space-y-1">
                    {items.map((post) => (
                      <button
                        key={post.id}
                        type="button"
                        draggable={canEdit}
                        onDragStart={(event) => {
                          event.dataTransfer.setData("application/x-planned-post", post.id);
                          event.dataTransfer.effectAllowed = "move";
                        }}
                        onClick={() => openPost(post)}
                        onContextMenu={postMenu.open(post)}
                        className={cn(
                          "flex w-full items-center gap-1 rounded-md border px-1 py-0.5 text-left text-[11px] leading-tight",
                          STATUS_STYLE[post.status],
                          canEdit && "cursor-grab active:cursor-grabbing",
                        )}
                        title={`${timeOf(post.scheduledAt)} · ${PLAN_CHANNEL_LABELS[post.channel]} · ${PLAN_STATUS_LABELS[post.status]}`}
                      >
                        <ChannelBadge channel={post.channel} />
                        <span className="shrink-0 tabular-nums opacity-70">
                          {timeOf(post.scheduledAt)}
                        </span>
                        <span className="min-w-0 flex-1 truncate font-medium">{post.title}</span>
                        {post.document?.thumbnailUrl ? (
                          <img
                            src={post.document.thumbnailUrl}
                            alt=""
                            className="size-4 shrink-0 rounded-sm object-cover"
                          />
                        ) : null}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <PlanList
          posts={(posts.data ?? []).filter(
            (post) => post.scheduledAt.getMonth() === month.getMonth(),
          )}
          onOpen={openPost}
          onMenu={postMenu.open}
        />
      )}

      <ContextMenuPopup at={postMenu.at} onClose={postMenu.close}>
        {postMenu.target ? (
          <>
            <DropdownLabel>{postMenu.target.title}</DropdownLabel>
            <DropdownItem onSelect={() => postMenu.target && openPost(postMenu.target)}>
              <Pencil /> {canEdit ? "Bearbeiten …" : "Ansehen"}
            </DropdownItem>
            {postMenu.target.document ? (
              <DropdownItem
                onSelect={() => {
                  const documentId = postMenu.target?.document?.id;
                  if (documentId)
                    void navigate({
                      to: "/o/$orgId/documents/$documentId",
                      params: { orgId, documentId },
                    });
                }}
              >
                <FileImage /> Datei öffnen
              </DropdownItem>
            ) : null}
            {canEdit ? (
              <>
                <DropdownSub>
                  <DropdownSubTrigger>
                    <Check /> Status
                  </DropdownSubTrigger>
                  <DropdownSubContent>
                    {PLAN_STATUSES.map((status) => (
                      <DropdownItem
                        key={status}
                        onSelect={() =>
                          postMenu.target &&
                          update.mutate({ orgId, id: postMenu.target.id, status })
                        }
                      >
                        <Check
                          className={cn(status === postMenu.target?.status ? "" : "invisible")}
                        />
                        {PLAN_STATUS_LABELS[status]}
                      </DropdownItem>
                    ))}
                  </DropdownSubContent>
                </DropdownSub>
                <DropdownSeparator />
                <DropdownItem danger onSelect={() => setRemoving(postMenu.target)}>
                  <Trash2 /> Löschen …
                </DropdownItem>
              </>
            ) : null}
          </>
        ) : null}
      </ContextMenuPopup>
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        danger
        title={`„${removing?.title ?? "Beitrag"}“ löschen?`}
        description="Nur der Eintrag im Redaktionsplan wird gelöscht, die verknüpfte Datei bleibt."
        confirmLabel="Löschen"
        onConfirm={async () => {
          if (!removing) return;
          await remove.mutateAsync({ orgId, id: removing.id });
          await invalidate();
          toast.success("Beitrag gelöscht");
        }}
      />
      {draft ? (
        <PostDialog
          orgId={orgId}
          draft={draft}
          canEdit={canEdit}
          onClose={() => {
            setDraft(null);
            if (plan) void navigate({ to: "/o/$orgId/planning", params: { orgId }, search: {} });
          }}
          onSaved={() => void invalidate()}
          onOpenFile={(documentId) =>
            void navigate({
              to: "/o/$orgId/documents/$documentId",
              params: { orgId, documentId },
            })
          }
        />
      ) : null}
    </>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition",
        active
          ? "border-brand-500 bg-brand-50 text-brand-700"
          : "border-zinc-200 text-zinc-600 hover:border-zinc-300",
      )}
    >
      {children}
    </button>
  );
}

function PlanList({
  posts,
  onOpen,
  onMenu,
}: {
  posts: PlannedPost[];
  onOpen: (post: PlannedPost) => void;
  onMenu: (post: PlannedPost) => (event: React.MouseEvent) => void;
}) {
  if (posts.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">
        In diesem Monat ist noch nichts geplant.
      </p>
    );
  }
  const formatDay = new Intl.DateTimeFormat("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
  return (
    <div className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200 bg-white">
      {posts.map((post) => (
        <button
          key={post.id}
          type="button"
          onClick={() => onOpen(post)}
          onContextMenu={onMenu(post)}
          className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-zinc-50"
        >
          <span className="w-32 shrink-0 text-sm whitespace-nowrap text-zinc-500 tabular-nums">
            {formatDay.format(post.scheduledAt)} {timeOf(post.scheduledAt)}
          </span>
          <ChannelBadge channel={post.channel} className="size-5 text-[9px]" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-zinc-900">{post.title}</span>
            {post.caption ? (
              <span className="block truncate text-xs text-zinc-500">{post.caption}</span>
            ) : null}
          </span>
          {post.assignee ? (
            <span className="shrink-0 text-xs text-zinc-500">{post.assignee.name}</span>
          ) : null}
          <span
            className={cn(
              "shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium",
              STATUS_STYLE[post.status],
            )}
          >
            {PLAN_STATUS_LABELS[post.status]}
          </span>
          {post.document?.thumbnailUrl ? (
            <img
              src={post.document.thumbnailUrl}
              alt=""
              className="size-9 shrink-0 rounded object-cover"
            />
          ) : null}
        </button>
      ))}
    </div>
  );
}

function PostDialog({
  orgId,
  draft: initial,
  canEdit,
  onClose,
  onSaved,
  onOpenFile,
}: {
  orgId: string;
  draft: Draft;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
  onOpenFile: (documentId: string) => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const files = useQuery(orpc.documents.recent.queryOptions({ input: { orgId, limit: 50 } }));
  const members = useQuery(orpc.comments.mentionable.queryOptions({ input: { orgId } }));
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const done = () => {
    onSaved();
    onClose();
  };
  const create = useMutation(
    orpc.contentPlan.create.mutationOptions({
      onSuccess: () => {
        toast.success("Beitrag geplant");
        done();
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  const update = useMutation(
    orpc.contentPlan.update.mutationOptions({
      onSuccess: () => {
        toast.success("Gespeichert");
        done();
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  const remove = useMutation(
    orpc.contentPlan.remove.mutationOptions({
      onSuccess: () => {
        toast.success("Beitrag gelöscht");
        done();
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );

  const limit = PLAN_CHANNEL_TEXT_LIMITS[draft.channel];
  const length = postTextLength(draft.caption);
  const tags = hashtagCount(draft.caption);
  const tagLimit = PLAN_CHANNEL_HASHTAG_LIMITS[draft.channel];
  const linked = files.data?.find((file) => file.id === draft.documentId);
  // „Einplanen …“ from the editor: the file's title is a good start.
  const [prefilled, setPrefilled] = useState(false);
  if (!prefilled && !draft.id && !draft.title && linked) {
    setPrefilled(true);
    setDraft({ ...draft, title: linked.title });
  }

  const save = () => {
    const [year, monthNumber, day] = draft.date.split("-").map(Number);
    const [hours, minutes] = draft.time.split(":").map(Number);
    if (!year || !monthNumber || !day) {
      toast.error("Bitte ein Datum angeben");
      return;
    }
    const values = {
      orgId,
      title: draft.title,
      scheduledAt: new Date(year, monthNumber - 1, day, hours ?? 10, minutes ?? 0),
      channel: draft.channel,
      status: draft.status,
      caption: draft.caption,
      notes: draft.notes,
      documentId: draft.documentId || null,
      assigneeId: draft.assigneeId || null,
    };
    if (draft.id) update.mutate({ ...values, id: draft.id });
    else create.mutate(values);
  };
  const busy = create.isPending || update.isPending;

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent
        title={draft.id ? (canEdit ? "Beitrag bearbeiten" : "Beitrag") : "Beitrag planen"}
        className="max-h-[calc(100vh-2rem)] max-w-2xl overflow-y-auto"
      >
        <fieldset disabled={!canEdit} className="space-y-3">
          <label className="block space-y-1">
            <span className="text-sm font-semibold text-zinc-700">Titel</span>
            <Input
              value={draft.title}
              onChange={(event) => set({ title: event.target.value })}
              placeholder="z. B. Sommeraktion – Teaser"
              autoFocus
            />
          </label>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <label className="block space-y-1">
              <span className="text-sm font-semibold text-zinc-700">Datum</span>
              <Input
                type="date"
                value={draft.date}
                onChange={(event) => set({ date: event.target.value })}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-semibold text-zinc-700">Uhrzeit</span>
              <Input
                type="time"
                value={draft.time}
                onChange={(event) => set({ time: event.target.value })}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-semibold text-zinc-700">Kanal</span>
              <Select
                value={draft.channel}
                onChange={(event) => set({ channel: event.target.value as PlanChannel })}
              >
                {PLAN_CHANNELS.map((channel) => (
                  <option key={channel} value={channel}>
                    {PLAN_CHANNEL_LABELS[channel]}
                  </option>
                ))}
              </Select>
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-semibold text-zinc-700">Status</span>
              <Select
                value={draft.status}
                onChange={(event) => set({ status: event.target.value as PlanStatus })}
              >
                {PLAN_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {PLAN_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1">
              <span className="text-sm font-semibold text-zinc-700">Datei</span>
              <Select
                value={draft.documentId}
                onChange={(event) => set({ documentId: event.target.value })}
              >
                <option value="">Keine Datei</option>
                {initial.documentId &&
                !files.data?.some((file) => file.id === initial.documentId) ? (
                  <option value={initial.documentId}>Verknüpfte Datei</option>
                ) : null}
                {files.data?.map((file) => (
                  <option key={file.id} value={file.id}>
                    {file.title} · {file.projectName}
                  </option>
                ))}
              </Select>
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-semibold text-zinc-700">Zuständig</span>
              <Select
                value={draft.assigneeId}
                onChange={(event) => set({ assigneeId: event.target.value })}
              >
                <option value="">Niemand</option>
                {members.data?.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </Select>
            </label>
          </div>
          {draft.documentId ? (
            <div className="flex items-center gap-3 rounded-lg border border-zinc-200 p-2">
              {linked?.thumbnailUrl ? (
                <img src={linked.thumbnailUrl} alt="" className="size-14 rounded object-cover" />
              ) : (
                <span className="flex size-14 items-center justify-center rounded bg-zinc-100 text-zinc-400">
                  {linked?.kind === "video" ? <Film /> : <FileImage />}
                </span>
              )}
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {linked?.title ?? "Verknüpfte Datei"}
              </span>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => onOpenFile(draft.documentId)}
              >
                Im Editor öffnen
              </Button>
            </div>
          ) : null}
          <label className="block space-y-1">
            <span className="flex items-baseline justify-between text-sm font-semibold text-zinc-700">
              Beitragstext und Hashtags
              <span
                className={cn(
                  "text-xs font-normal tabular-nums",
                  (limit !== null && length > limit) || (tagLimit !== undefined && tags > tagLimit)
                    ? "text-red-600"
                    : "text-zinc-500",
                )}
              >
                {length.toLocaleString("de-DE")}
                {limit !== null ? ` / ${limit.toLocaleString("de-DE")}` : ""} Zeichen · {tags}
                {tagLimit !== undefined ? ` / ${tagLimit}` : ""} Hashtags
              </span>
            </span>
            <Textarea
              value={draft.caption}
              onChange={(event) => set({ caption: event.target.value })}
              className="min-h-28"
              placeholder="Text für den Beitrag … #hashtag"
            />
            {limit !== null && length > limit ? (
              <span className="block text-xs text-red-600">
                Zu lang für {PLAN_CHANNEL_LABELS[draft.channel]} (höchstens{" "}
                {limit.toLocaleString("de-DE")} Zeichen).
              </span>
            ) : null}
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-semibold text-zinc-700">Notizen</span>
            <Textarea
              value={draft.notes}
              onChange={(event) => set({ notes: event.target.value })}
              placeholder="Intern, z. B. Freigabe durch Kundin bis Mittwoch"
            />
          </label>
        </fieldset>
        <DialogFooter>
          {draft.id && canEdit ? (
            <Button
              variant="ghost"
              className="mr-auto text-red-600"
              onClick={() => setConfirmDelete(true)}
            >
              Löschen
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            {canEdit ? "Abbrechen" : "Schließen"}
          </Button>
          {canEdit ? (
            <Button loading={busy} disabled={!draft.title.trim()} onClick={save}>
              {draft.id ? "Speichern" : "Einplanen"}
            </Button>
          ) : null}
        </DialogFooter>
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title="Beitrag löschen?"
          description={`„${draft.title}“ wird aus der Planung entfernt. Eine verknüpfte Datei bleibt erhalten.`}
          confirmLabel="Löschen"
          danger
          onConfirm={() => draft.id && remove.mutate({ orgId, id: draft.id })}
        />
      </DialogContent>
    </Dialog>
  );
}
